import { H3 } from 'common'
import { ArvBeitrag, MemberData } from 'hooks/transformToJson'
import * as React from 'react'
import { Card, Col, Row, Table } from 'react-bootstrap'
import { BillingData } from '../../MemberDataTypes'

import styles from './ActiveMembershipTable.module.scss'

export interface Props {
    billingsData?: BillingData | null
    memberData?: MemberData | MemberData[] | undefined
}

const filterEmptyBeitrag = (fee: unknown): boolean => {
    return fee !== undefined && typeof fee === 'object' && fee !== null && 'ZahlungAb' in fee
}

const convertToDate = (value: string | null | undefined): Date | null => {
    if (!value) {
        return null
    }

    if (!/^\d{4}-\d{2}$/.test(value)) {
        return null
    }

    const [year, month] = value.split('-').map(Number)

    if (month < 1 || month > 12) {
        return null
    }

    return new Date(year, month - 1, 1)
}

const isValueInFuture = (value: string | null | undefined): boolean => {
    const valueAsDate = convertToDate(value)

    if (!valueAsDate) {
        return false
    }

    return valueAsDate > new Date()
}

const renderDate = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yyyy = date.getFullYear()
    return `${mm}.${yyyy}`
}

const renderBeitragTimeWindow = (beitrag: ArvBeitrag): string => {
    const zahlungAb = convertToDate(beitrag.ZahlungAb._text)
    const zahlungBis = convertToDate(beitrag.ZahlungBis._text)

    if (!zahlungBis && !zahlungAb) {
        return ''
    }

    if (zahlungAb && zahlungBis) {
        return `(ab ${renderDate(zahlungBis)} bis ${renderDate(zahlungBis)})`
    }

    if (zahlungAb) {
        return `(ab ${renderDate(zahlungAb)})`
    }

    if (zahlungBis) {
        return `(bis ${renderDate(zahlungBis)})`
    }

    return ''
}

const ActiveMembershipTable = ({ billingsData, memberData }: Props): JSX.Element => {
    const [summary, setSummary] = React.useState<number>(-1)
    const [summaryFamily, setSummaryFamily] = React.useState<number>(0)

    React.useEffect((): void => {
        let feeSummary = 0
        let familySummary = 0

        billingsData?.Beitraege.Beitrag.forEach((fee) => {
            // skip fees that are not yet active
            if (!fee || !fee.ZahlungAb || isValueInFuture(fee.ZahlungAb._text)) {
                return
            }

            const currentFee = !fee.Betrag?._text ? 0 : parseFloat(fee.Betrag?._text)
            feeSummary += currentFee
        })
        setSummary(feeSummary)

        if (Array.isArray(memberData)) {
            memberData.forEach((member) => {
                if (member.Readonly.Beitraege && member.Readonly.Beitraege.Beitrag) {
                    let beitraege = member.Readonly.Beitraege.Beitrag

                    // we expect an array, so we ensure this here, in case API returns differently
                    if (!Array.isArray(beitraege)) {
                        beitraege = [beitraege]
                    }

                    beitraege.forEach((fee) => {
                        // skip fees that are not yet active
                        if (!fee || !fee.ZahlungAb || isValueInFuture(fee.ZahlungAb._text)) {
                            return
                        }

                        const currentFee = !fee.Betrag?._text ? 0 : parseFloat(fee.Betrag?._text)
                        familySummary += currentFee
                    })
                }
            })
            setSummaryFamily(familySummary)
        } else {
            setSummaryFamily(-1)
        }
    }, [billingsData])

    if (!billingsData || !billingsData.Beitraege.Beitrag.length) {
        return <></>
    }

    const renderFees = (fees: ArvBeitrag[]): JSX.Element[] => {
        return fees.map((fee) => {
            const currentFee = !fee.Betrag?._text ? 0 : parseFloat(fee.Betrag?._text)

            if (fee.Name._text?.includes('Hauptverein')) {
                return (
                    <tr key={fee.Name._text?.toLowerCase()}>
                        <th colSpan={2} className="border-top-0">
                            {fee.Name._text || 'Vereinsbeitrag'} {renderBeitragTimeWindow(fee)}
                        </th>
                        <td className="text-right border-top-0">
                            {currentFee.toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                            &nbsp;€
                        </td>
                    </tr>
                )
            }

            return (
                <tr key={fee.Name._text?.toLowerCase()}>
                    <td className="text-right">+</td>
                    <td>
                        {fee.Name._text} {renderBeitragTimeWindow(fee)}
                    </td>
                    <td className="text-right">
                        {currentFee.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                        &nbsp;€
                    </td>
                </tr>
            )
        })
    }

    const renderFutureFees = (): JSX.Element => {
        const futureFees = billingsData.Beitraege.Beitrag.filter(filterEmptyBeitrag).filter((fee) =>
            isValueInFuture(fee.ZahlungAb._text),
        )

        if (futureFees?.length === 0) {
            return <></>
        }

        return (
            <Col>
                <p>
                    <H3>zukünftige Mitgliedsbeiträge</H3>
                    <Card className={styles.billWrapper}>
                        <Card.Body className="p-0">
                            <Table responsive>
                                <tbody>{renderFees(futureFees)}</tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </p>
            </Col>
        )
    }

    const renderCurrentFees = (): JSX.Element => {
        const currentFees = billingsData.Beitraege.Beitrag.filter(filterEmptyBeitrag).filter(
            (fee) => !isValueInFuture(fee.ZahlungAb._text),
        )

        return (
            <Col>
                <p>
                    <H3>monatliche Mitgliedsbeiträge</H3>
                    <Card className={styles.billWrapper}>
                        <Card.Body className="p-0">
                            <Table responsive>
                                <tbody>
                                    {renderFees(currentFees)}
                                    <tr>
                                        <th colSpan={2}>Summe Mitglied</th>
                                        <th className="text-right">
                                            {summary.toLocaleString('de-DE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                            &nbsp;€
                                        </th>
                                    </tr>
                                    {
                                        // Einzug gesamt
                                        summaryFamily >= 0 && (
                                            <tr>
                                                <th colSpan={2}>Gesamtsumme Familie</th>
                                                <th className="text-right">
                                                    {summaryFamily.toLocaleString('de-DE', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                    &nbsp;€
                                                </th>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </p>
            </Col>
        )
    }

    return (
        <>
            <Row>
                {renderCurrentFees()}
                {renderFutureFees()}
            </Row>
            <p>
                Die Abbuchung erfolgt monatlich jeweils am 10. Bankarbeitstag. Rückwirkende Beiträge sind am 10.
                Bankarbeitstag des Folgemonats fällig.
            </p>
            <p>
                Die Aufnahmegebühr sowie sonstige Gebühren (Fitnesscheck, Tennisaufnahme etc) sind mit dem ersten
                Beitragseinzug fällig. Die jeweilige Höhe des Beitrags, der Aufnahmegebühr und sonstige Gebühren werden
                nach der im Sportprogramm enthaltenen Beitrags- und Gebührenauflistung (
                <a
                    href="https://www.niendorfer-tsv.de/mitgliedschaft/beitraege.html"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    mtl. Beiträge
                </a>
                ,{' '}
                <a
                    href="https://www.niendorfer-tsv.de/mitgliedschaft/zusatzbeitraege-allgemein.html"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Zusatzbeiträge
                </a>
                ) geregelt.
            </p>
        </>
    )
}

export default ActiveMembershipTable
